import * as React from 'react';
import { Popup } from 'semantic-ui-react';
import { Dataset, DatasetResource } from '../../../shared/api/api';
import { addModifiers, formatMonthYear } from '../../../shared/helpers';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { getSingleDatasetPath } from '../dataExplorerRouterPaths';
import { renderCoverageCell, renderDateCoverageCell } from '../DataTableCommon/DataTableCommon';
import { DataTableColumn } from '../DataTableCommon/DataTableCommonTypes';
import { DataFieldLabels } from '../DatasetFieldsListTable/DatasetFieldsListTableColumns';
import { DataExplorerLink } from '../dataExplorerHistoryContext';
import { CellContext } from '@tanstack/react-table';
import groupBy from 'lodash/groupBy';
import { renderEmojiByResourceType } from '../Dataset/ListResources';

export function getDatasetsListColumns(
  trackEvent: (event: string) => void,
  isPyramidMultiplierVisible: boolean
): DataTableColumn<Dataset>[] {
  return [
    {
      id: 'name',
      name: 'dataExplorer.datasetFields.name',
      accessorFn: (dataset: Dataset) => dataset.name,
      active: true,
      display: true,
      type: 'choices',
      size: 150,
      cell: rowData => {
        const row = rowData.row;
        const { themes, new: isNew, id: datasetId } = row.original;
        return (
          <div
            onClickCapture={() => {
              trackEvent('de_click_dataset');
            }}
            className={addModifiers('data-table__cell-content', ['with-wrapping'])}
          >
            <DataExplorerLink className={addModifiers('link', ['wrap'])} to={getSingleDatasetPath({ datasetId })}>
              {row.getValue('name')}
            </DataExplorerLink>
            <DataFieldLabels isNew={isNew} themes={themes} />
          </div>
        );
      },
    },
    {
      id: 'fieldCount',
      name: 'dataExplorer.datasetFields.fieldCount',
      accessorFn: (dataset: Dataset) => dataset.fieldCount,
      active: true,
      display: true,
      type: 'integer',
      size: 45,
      cell: rowData => {
        const row = rowData.row;
        return <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>{row.getValue('fieldCount')}</div>;
      },
    },
    ...(isPyramidMultiplierVisible
      ? [
          {
            id: 'pyramidMultiplier',
            name: 'dataExplorer.datasetFields.pyramidMultiplier',
            accessorFn: (dataset: Dataset) => dataset.pyramidMultiplier,
            active: true,
            display: true,
            enableSorting: false,
            type: 'decimal',
            size: 45,
            cell: (rowData: CellContext<Dataset, Dataset>) => {
              const row = rowData.row;
              return <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>{row.getValue('pyramidMultiplier')}</div>;
            },
          },
        ]
      : []),

    {
      id: 'coverage',
      name: 'dataExplorer.datasetFields.coverage',
      accessorFn: (dataset: Dataset) => dataset.coverage,
      active: true,
      display: true,
      type: 'percent',
      size: 55,
      cell: renderCoverageCell<Dataset>,
    },
    {
      id: 'dateCoverage',
      name: 'dataExplorer.datasetFields.dateCoverage',
      accessorFn: (dataset: Dataset) => dataset.dateCoverage,
      active: true,
      display: true,
      type: 'percent',
      size: 55,
      cell: renderDateCoverageCell<Dataset>,
    },
    {
      id: 'valueScore',
      name: 'dataExplorer.datasetFields.valueScore',
      accessorFn: (dataset: Dataset) => dataset.valueScore,
      active: true,
      display: true,
      type: 'integer',
      size: 45,
      cell: rowData => {
        const row = rowData.row;
        return <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>{row.getValue('valueScore')}</div>;
      },
    },
    {
      id: 'alphaCount',
      name: 'dataExplorer.datasetFields.alphaCount',
      accessorFn: (dataset: Dataset) => dataset.alphaCount,
      active: true,
      display: true,
      type: 'integer',
      size: 45,
      cell: rowData => {
        const row = rowData.row;
        return <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>{row.getValue('alphaCount')}</div>;
      },
    },
    {
      id: 'dateUpdated',
      name: 'dataExplorer.datasetFields.lastFieldAdded',
      accessorFn: (dataset: Dataset) => dataset.dateUpdated,
      active: true,
      display: true,
      type: 'choices',
      size: 60,
      cell: rowData => {
        const row = rowData.row;
        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>{formatMonthYear(row.getValue('dateUpdated'))}</div>
        );
      },
    },
    {
      id: 'researchPapers',
      name: 'dataExplorer.datasetFields.resources',
      accessorFn: (dataset: Dataset) => dataset.researchPapers,
      active: true,
      display: true,
      type: 'array',
      size: 60,
      enableSorting: false,
      cell: rowData => {
        const row = rowData.row;
        const resources = row.getValue('researchPapers') as DatasetResource[];
        const resourcesByType = groupBy(resources, 'type');

        return (
          <div
            className={addModifiers('data-table__cell-content', ['with-wrapping', 'resources'])}
            // Since this cell is interactive, it will enhance the space that users can interact with and prevent mis-clicks on the row
            onClick={e => e.stopPropagation()}
          >
            {Object.entries(resourcesByType).map(([type, resources]) => (
              <div key={type}>
                {renderEmojiByResourceType(type)}
                {resources.map((resource, index, list) => (
                  <div key={resource.url}>
                    <Popup
                      on="hover"
                      size="tiny"
                      basic={true}
                      inverted={true}
                      content={resource.title}
                      trigger={
                        <a target="_blank" rel="noreferrer" href={resource.url} className="link" onClick={e => e.stopPropagation()}>
                          <FormattedMessageHelper id="dataExplorer.datasetFields.resources.link" values={{ index: index + 1 }} />
                        </a>
                      }
                    />
                    {index !== list.length - 1 && <span>,</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      },
    },
  ];
}
